import React, { useContext, useState } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import Ic from "../common/Ic";
import Avatar from "../common/Avatar";
import { formatPropAddr } from "../../utils/propertyAddr";
import { isCommercial, ptLabel } from "../../utils/propertyTypes";
import { IS_DESKTOP } from "../../constants/platform";

/* ─── Canonical app base URL for property deep-links ───────────────────── */
const APP_BASE_URL = "https://www.prorentz.com";
const propLink = (propId) => `${APP_BASE_URL}/?property=${propId}`;
const LOGO_URL   = "/logo192.png";

/* ─── Canvas helpers — load image + brand-watermark each property photo ── */
const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload  = () => resolve(img);
  img.onerror = (e) => reject(e);
  img.src = url;
});

// Composites the House Rental logo into the bottom-right corner of a
// property photo. Returns a JPEG Blob ready to wrap in a File.
const watermarkPhoto = async (photoUrl, logoImg) => {
  const photo  = await loadImage(photoUrl);
  const W      = photo.naturalWidth  || photo.width;
  const H      = photo.naturalHeight || photo.height;
  const canvas = document.createElement("canvas");
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(photo, 0, 0);

  // Logo size: 8% of width, clamped 48-95px — subtle brand mark, not
  // a billboard. Padding sits at ~28% of logo size so the white pill
  // looks balanced on photos of any aspect ratio.
  const size = Math.max(48, Math.min(W * 0.08, 95));
  const pad  = size * 0.28;
  const x    = W - size - pad;
  const y    = H - size - pad;

  // soft drop shadow on the white pill behind logo (for legibility on
  // both dark + light photo backgrounds)
  const bgPad = Math.round(size * 0.12);
  ctx.save();
  ctx.shadowColor   = "rgba(0,0,0,0.22)";
  ctx.shadowBlur    = 16;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle     = "rgba(255,255,255,0.94)";
  const rx = x - bgPad, ry = y - bgPad;
  const rw = size + bgPad * 2, rh = size + bgPad * 2;
  const r  = Math.round(size * 0.18);
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(rx, ry, rw, rh, r);
    ctx.fill();
  } else {
    ctx.fillRect(rx, ry, rw, rh);
  }
  ctx.restore();

  ctx.drawImage(logoImg, x, y, size, size);

  return await new Promise((resolve, reject) => {
    canvas.toBlob(b => b ? resolve(b) : reject(new Error("toBlob failed")), "image/jpeg", 0.92);
  });
};

/* ─── Share icon SVG (network / share nodes) ───────────────────────────── */
const ShareIcon = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

/* ─── Styled checkbox row ───────────────────────────────────────────────── */
function ShareOption({ checked, onChange, icon, label, extra, h, children }) {
  return (
    <label style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "11px 12px", borderRadius: 12,
      background: checked ? `${h.ac}0D` : h.sf,
      border: `1px solid ${checked ? h.ac + "35" : h.bd}`,
      cursor: "pointer", transition: "background 0.15s, border-color 0.15s",
      flexDirection: "column",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
        {/* custom checkbox */}
        <div style={{
          width: 20, height: 20, borderRadius: 6, flexShrink: 0,
          border: `2px solid ${checked ? h.ac : h.b2}`,
          background: checked ? h.ac : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.15s",
        }}>
          {checked && (
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"
              stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,6 5,9 10,3"/>
            </svg>
          )}
        </div>
        <input type="checkbox" checked={checked} onChange={onChange} style={{ display: "none" }} />
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ color: h.tx, fontSize: 13, fontWeight: 500, flex: 1 }}>
          {label}
          {extra && <span style={{ color: h.ts, fontWeight: 400, fontSize: 12 }}> {extra}</span>}
        </span>
      </div>
      {/* slot for inline sub-input (contact number) */}
      {checked && children && <div style={{ width: "100%", paddingLeft: 30 }}>{children}</div>}
    </label>
  );
}

function PropsListPage() {
  const { t, h, props, setProps, tens, setTens, go, setSelPropId, flash, showConfirm } = useContext(Ctx);

  /* ── share modal state ─────────────────────────────────────────────── */
  const [shareTarget, setShareTarget]   = useState(null);  // property object | null
  const [shareOpts, setShareOpts]       = useState({ address: true, rent: true, rooms: true, amenities: true, contact: false });
  // Set of selected photo indexes (0-based, capped at 6). Empty set
  // means no photos will be attached. Photos are now per-image
  // selectable instead of a single all-or-nothing toggle.
  const [photoSel, setPhotoSel]         = useState(new Set());
  const [contactNum, setContactNum]     = useState("");
  const [sharing, setSharing]           = useState(false);
  const [photoErr, setPhotoErr]         = useState(false);

  const openShareModal = (p) => {
    const hasAmen    = !!(p.amen && p.amen.length);
    setShareTarget(p);
    setPhotoErr(false);
    setShareOpts({
      address:   true,
      rent:      true,
      rooms:     true,
      amenities: hasAmen,
      contact:   false,
    });
    setPhotoSel(new Set()); // start with no photos selected
  };

  const toggle = (key) => setShareOpts(s => ({ ...s, [key]: !s[key] }));

  // Photos: master toggles all on/off. Cap at 6 (Web Share file limit).
  const togglePhotoMaster = () => {
    if (!shareTarget?.photos?.length) return;
    if (photoSel.size > 0) {
      setPhotoSel(new Set());
    } else {
      const all = new Set();
      shareTarget.photos.slice(0, 6).forEach((_, i) => all.add(i));
      setPhotoSel(all);
    }
    setPhotoErr(false);
  };

  // Individual photo toggle
  const togglePhotoIdx = (idx) => {
    setPhotoSel(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
    setPhotoErr(false);
  };

  // Build the share MESSAGE body.
  // The URL is intentionally OMITTED here — it's passed separately as
  // `shareData.url` to navigator.share() so the receiving app (WhatsApp,
  // FB, Instagram) shows a rich link preview and the link appears only
  // once at the end of the message. Including it in both `text` and
  // `url` caused the duplicate URL the user saw on WhatsApp.
  const buildText = (p, opts) => {
    const lines = ["🏠 Property Available for Rent", ""];
    if (opts.address)                      lines.push(`📍 ${formatPropAddr(p)}`);
    if (opts.rooms)                        lines.push(`🛏️ ${p.rooms} ${t.rooms || "Rooms"}`);
    if (opts.rent)                         lines.push(`💰 ₹${p.rent.toLocaleString()}/mo  ·  Deposit: ₹${p.dep.toLocaleString()}`);
    if (opts.amenities && p.amen?.length)  lines.push(`✅ ${p.amen.join(", ")}`);
    if (opts.contact && contactNum.trim()) lines.push(`📞 ${contactNum.trim()}`);
    lines.push("", "👇 Tap the link below to view the full listing.");
    lines.push("Interested? Contact me for details!");
    // collapse consecutive blank lines
    return lines.filter((v, i, a) => !(v === "" && a[i - 1] === "")).join("\n");
  };

  const doShare = async () => {
    if (!shareTarget) return;
    setSharing(true);
    setPhotoErr(false);
    const p    = shareTarget;
    const text = buildText(p, shareOpts);
    const addr = formatPropAddr(p);

    /* ── prepare image attachments ─────────────────────────────────── */
    // House Rental logo is MANDATORY in every share. When the user has
    // selected photos, we composite the logo into the bottom-right
    // corner of each photo (brand watermark — every shared image
    // carries the House Rental mark). When no photos are selected we
    // attach the logo as a single standalone image so the brand still
    // appears in the share.
    let files   = [];
    let logoImg = null;
    try {
      logoImg = await loadImage(`${window.location.origin}${LOGO_URL}`);
    } catch (_) { /* logo load failed — share continues without brand */ }

    // Only the user-selected photo indexes are processed (preserves the
    // order they appear in the property's photo array).
    const selectedPhotos = p.photos?.length
      ? p.photos.slice(0, 6).filter((_, i) => photoSel.has(i))
      : [];

    if (selectedPhotos.length) {
      const results = await Promise.allSettled(
        selectedPhotos.map(async (url, i) => {
          let blob;
          try {
            blob = logoImg
              ? await watermarkPhoto(url, logoImg)
              : await (await fetch(url)).blob();
          } catch (_) {
            const res = await fetch(url);
            blob = await res.blob();
          }
          return new File([blob], `property-${i + 1}.jpg`, { type: blob.type || "image/jpeg" });
        })
      );
      const photoFiles = results.filter(r => r.status === "fulfilled").map(r => r.value);
      files = photoFiles;
      if (!photoFiles.length) setPhotoErr(true);
    } else if (logoImg) {
      // No photos chosen — attach the logo as its own image so the
      // brand is still present in the share
      try {
        const canvas = document.createElement("canvas");
        canvas.width  = logoImg.naturalWidth  || 192;
        canvas.height = logoImg.naturalHeight || 192;
        canvas.getContext("2d").drawImage(logoImg, 0, 0);
        const logoBlob = await new Promise((res, rej) =>
          canvas.toBlob(b => b ? res(b) : rej(), "image/png")
        );
        files.push(new File([logoBlob], "house-rental-logo.png", { type: "image/png" }));
      } catch (_) {
        try {
          const res = await fetch(`${window.location.origin}${LOGO_URL}`);
          const blob = await res.blob();
          files.push(new File([blob], "house-rental-logo.png", { type: "image/png" }));
        } catch (_) { /* give up — share text + url only */ }
      }
    }

    const shareData = { title: `Property – ${addr}`, text, url: propLink(p.id) };
    // Only attach files if the browser/OS can handle them
    if (files.length && navigator.canShare && navigator.canShare({ files })) {
      shareData.files = files;
    } else if (files.length && photoSel.size > 0) {
      // files fetched but canShare({files}) not supported → text + url only
      setPhotoErr(true);
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Clipboard fallback has no `url` field, so append the link to
        // the text payload so users still get a complete message.
        await navigator.clipboard.writeText(`${text}\n\n${propLink(p.id)}`);
        flash("Copied to clipboard — paste anywhere to share!");
      }
    } catch (_) { /* user cancelled */ }

    setSharing(false);
    setShareTarget(null);
  };

  /* ── delete ─────────────────────────────────────────────────────────── */
  const deleteProp = (pid) => {
    showConfirm(t.deletePropTenConfirm, () => {
      api.deleteProperty(pid);
      setProps(props.filter(p => p.id !== pid));
      setTens(tens.map(te => te.pid === pid ? { ...te, pid: null, vac: false } : te));
      flash(t.propDeleted);
    });
  };

  /* ── preview text (live, derived from current shareOpts) ─────────── */
  const previewText = shareTarget ? buildText(shareTarget, shareOpts) : "";

  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ paddingTop: 16 }}>

      {/* ── SHARE BOTTOM SHEET ─────────────────────────────────────── */}
      {shareTarget && (
        <div
          onClick={() => !sharing && setShareTarget(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: h.cd, borderRadius: "20px 20px 0 0",
              width: "100%", maxWidth: 430, margin: "0 auto",
              padding: "16px 16px 36px",
              boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
              maxHeight: "90vh", overflowY: "auto",
            }}
          >
            {/* drag handle */}
            <div style={{ width: 36, height: 4, background: h.bd, borderRadius: 2, margin: "0 auto 16px" }} />

            {/* header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
              <ShareIcon size={18} color={h.ac} />
              <span style={{ color: h.tx, fontSize: 16, fontWeight: 700 }}>Share Property</span>
            </div>
            <div style={{ color: h.ts, fontSize: 12, marginBottom: 18, paddingLeft: 28 }}>{formatPropAddr(shareTarget)}</div>

            {/* ── MANDATORY BRAND BANNER (logo is non-toggleable) ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", borderRadius: 12,
              background: `linear-gradient(135deg, ${h.ac}14 0%, ${h.ac}08 100%)`,
              border: `1px solid ${h.ac}30`,
              marginBottom: 14,
            }}>
              <img src={LOGO_URL} alt="ProRentz"
                style={{ width: 38, height: 38, borderRadius: 9, border: `1px solid ${h.bd}`, objectFit: "contain", background: h.el, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>ProRentz brand</div>
                <div style={{ color: h.ts, fontSize: 11, lineHeight: 1.4 }}>
                  {shareTarget.photos?.length > 0 && photoSel.size > 0
                    ? `Logo will be watermarked on ${photoSel.size === 1 ? "the selected photo" : `each of the ${photoSel.size} selected photos`} (bottom-right corner)`
                    : "Logo will be attached as a brand image"}
                </div>
              </div>
            </div>

            {/* ── OPTION LIST ── */}
            <div style={{ color: h.ts, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>
              Select what to include
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>

              <ShareOption checked={shareOpts.address} onChange={() => toggle("address")}
                icon="📍" label="Address" h={h} />

              <ShareOption checked={shareOpts.rooms} onChange={() => toggle("rooms")}
                icon="🛏️" label={`${shareTarget.rooms} ${t.rooms || "Rooms"}`} h={h} />

              <ShareOption checked={shareOpts.rent} onChange={() => toggle("rent")}
                icon="💰" label="Rent & Deposit"
                extra={`₹${shareTarget.rent.toLocaleString()}/mo · Dep ₹${shareTarget.dep.toLocaleString()}`}
                h={h} />

              {shareTarget.amen?.length > 0 && (
                <ShareOption checked={shareOpts.amenities} onChange={() => toggle("amenities")}
                  icon="✅" label="Amenities"
                  extra={shareTarget.amen.slice(0, 3).join(", ") + (shareTarget.amen.length > 3 ? " …" : "")}
                  h={h} />
              )}

              {shareTarget.photos?.length > 0 && (() => {
                const total      = Math.min(shareTarget.photos.length, 6);
                const allOn      = photoSel.size === total && total > 0;
                const someOn     = photoSel.size > 0 && photoSel.size < total;
                const masterTint = photoSel.size > 0;
                return (
                  <div style={{
                    padding: "11px 12px", borderRadius: 12,
                    background: masterTint ? `${h.ac}0D` : h.sf,
                    border: `1px solid ${masterTint ? h.ac + "35" : h.bd}`,
                    transition: "background 0.15s, border-color 0.15s",
                  }}>
                    {/* Master row — toggles all on/off. */}
                    <div onClick={togglePhotoMaster}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                        border: `2px solid ${masterTint ? h.ac : h.b2}`,
                        background: allOn ? h.ac : someOn ? `${h.ac}55` : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.15s",
                      }}>
                        {allOn && (
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="2,6 5,9 10,3"/>
                          </svg>
                        )}
                        {someOn && (
                          <div style={{ width: 8, height: 2, background: "#fff", borderRadius: 1 }} />
                        )}
                      </div>
                      <span style={{ fontSize: 15 }}>📷</span>
                      <span style={{ color: h.tx, fontSize: 13, fontWeight: 500, flex: 1 }}>
                        Property Photos
                        <span style={{ color: h.ts, fontWeight: 400, fontSize: 12 }}>
                          {" "}{photoSel.size > 0
                            ? `${photoSel.size} of ${total} selected`
                            : `${shareTarget.photos.length} available — tap to select`}
                        </span>
                      </span>
                    </div>

                    {/* Always-visible thumbnail grid — each photo is independently tappable. */}
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", paddingLeft: 30 }}>
                      {shareTarget.photos.slice(0, 6).map((url, i) => {
                        const selected = photoSel.has(i);
                        return (
                          <div
                            key={i}
                            onClick={(e) => { e.stopPropagation(); togglePhotoIdx(i); }}
                            title={selected ? "Tap to remove from share" : "Tap to include in share"}
                            style={{
                              position: "relative",
                              cursor: "pointer",
                              transition: "transform 0.12s",
                              transform: selected ? "scale(1)" : "scale(0.96)",
                            }}
                          >
                            <img src={url} alt={`Photo ${i + 1}`}
                              style={{
                                width: 58, height: 58, borderRadius: 9,
                                objectFit: "cover", display: "block",
                                border: `2px solid ${selected ? h.ac : "transparent"}`,
                                opacity: selected ? 1 : 0.55,
                                boxShadow: selected ? `0 2px 8px ${h.ac}30` : "none",
                                transition: "all 0.15s",
                              }} />
                            {/* Selection checkmark badge */}
                            <div style={{
                              position: "absolute", top: -4, right: -4,
                              width: 19, height: 19, borderRadius: 10,
                              background: selected ? h.ac : "rgba(255,255,255,0.95)",
                              border: `2px solid ${selected ? "#fff" : h.b2}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                              transition: "all 0.15s",
                            }}>
                              {selected && (
                                <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="2,6 5,9 10,3"/>
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {photoErr && (
                      <div style={{ color: h.wn, fontSize: 11, marginTop: 8, paddingLeft: 30 }}>
                        ⚠️ Photos couldn't be attached — text will still be shared.
                      </div>
                    )}
                  </div>
                );
              })()}

              <ShareOption checked={shareOpts.contact} onChange={() => toggle("contact")}
                icon="📞" label="Your Contact Number" h={h}>
                <input
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={contactNum}
                  onChange={e => setContactNum(e.target.value)}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: h.ib, border: `1px solid ${h.b2}`,
                    borderRadius: 8, padding: "8px 10px",
                    color: h.tx, fontSize: 13, marginTop: 4,
                    outline: "none",
                  }}
                />
              </ShareOption>
            </div>

            {/* ── LIVE PREVIEW ── */}
            <div style={{ background: h.sf, borderRadius: 12, padding: "12px 14px", marginBottom: 18, border: `1px solid ${h.bd}` }}>
              <div style={{ color: h.ts, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>
                Preview
              </div>
              <pre style={{ color: h.tx, fontSize: 12, lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                {previewText}
              </pre>
              {/* footer: the property link that recipients will tap.
                  Shown only here (not in the text body) so the shared
                  message contains exactly ONE link, which previews as
                  a rich card with the property detail page. */}
              <div style={{ marginTop: 8, borderTop: `1px dashed ${h.bd}`, paddingTop: 8 }}>
                <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, wordBreak: "break-all" }}>
                  🔗 {propLink(shareTarget.id)}
                </div>
                <div style={{ color: h.ts, fontSize: 10, marginTop: 3 }}>
                  Tap → opens the property detail page directly
                </div>
              </div>
              <div style={{ color: h.ts, fontSize: 11, marginTop: 8 }}>
                📎 {photoSel.size > 0
                  ? `${photoSel.size} photo${photoSel.size > 1 ? "s" : ""} (with brand logo watermark)`
                  : "ProRentz brand logo"}
              </div>
            </div>

            {/* ── ACTIONS ── */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShareTarget(null)}
                disabled={sharing}
                style={{ flex: 1, background: h.sf, border: `1px solid ${h.bd}`, color: h.ts, padding: "13px", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={doShare}
                disabled={sharing}
                style={{
                  flex: 2, background: sharing ? `${h.ac}99` : h.ac,
                  border: "none", color: "#fff", padding: "13px", borderRadius: 12,
                  cursor: sharing ? "default" : "pointer", fontSize: 14, fontWeight: 600,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "opacity 0.2s",
                }}
              >
                <ShareIcon size={15} color="#fff" />
                {sharing ? "Sharing…" : "Share Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PAGE HEADER ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ color: h.tx, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: "-0.6px" }}>{t.properties}</h2>
        <button data-coach="props-add" onClick={() => go("addProp")}
          style={{ background: h.ac, border: "none", color: "#fff", padding: "9px 16px", borderRadius: 999, cursor: "pointer", fontSize: 12.5, fontWeight: 700, boxShadow: `0 6px 16px ${h.ac}40` }}>
          + {t.addProp}
        </button>
      </div>

      {/* ── PROPERTY CARDS ──────────────────────────────────────────── */}
      <div data-coach="props-list" style={IS_DESKTOP ? { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, alignItems: "start" } : undefined}>
        {props.map(p => {
          const te    = tens.find(x => x.id === p.tid);
          const isCom = isCommercial(p.property_type);
          const photo = p.photos && p.photos[0];
          return (
            <div key={p.id} style={{ background: h.cd, borderRadius: 22, overflow: "hidden", boxShadow: h.sh, marginBottom: IS_DESKTOP ? 0 : 16 }}>
              {/* photo banner — status pill + share float on the image */}
              <div
                onClick={() => { setSelPropId(p.id); go("propDetail"); }}
                style={{ position: "relative", aspectRatio: "16/9", background: photo ? "#000" : `${h.ac}0d`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", cursor: "pointer" }}
              >
                {photo
                  ? <img src={photo} alt={formatPropAddr(p)} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  : <Ic n="bldg" s={34} c={h.ac} />}
                <span style={{
                  position: "absolute", top: 10, left: 10,
                  background: p.occ ? "rgba(22,163,74,0.95)" : "rgba(244,63,94,0.95)", color: "#fff",
                  padding: "4px 10px", borderRadius: 999, fontSize: 9.5, fontWeight: 700,
                  letterSpacing: "0.4px", textTransform: "uppercase",
                  backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                }}>
                  {p.occ ? t.occupied : t.vacant}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); openShareModal(p); }}
                  title="Share property"
                  aria-label="Share property"
                  style={{
                    position: "absolute", top: 10, right: 10, width: 32, height: 32,
                    borderRadius: 16, background: "rgba(255,255,255,0.92)", border: "none",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                  }}
                >
                  <ShareIcon size={13} color={h.a2} />
                </button>
              </div>

              {/* info block */}
              <div style={{ padding: "14px 16px 16px" }}>
                <div style={{ color: h.tx, fontWeight: 700, fontSize: 15, letterSpacing: "-0.2px", lineHeight: 1.3 }}>{formatPropAddr(p)}</div>
                <div style={{ color: h.ts, fontSize: 12, marginTop: 4 }}>
                  {p.rooms} {t.rooms} · <span style={{ color: h.tx, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>₹{p.rent.toLocaleString()}</span>/mo
                  {isCom && p.area_sqft ? ` · ${p.area_sqft} sq.ft` : ""}
                </div>
                <div style={{ color: h.tt || h.sc, fontSize: 11, marginTop: 2 }}>Dep ₹{p.dep.toLocaleString()}</div>

                {/* type + amenity chips (first 3, then a count) */}
                <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                  {p.property_type && p.property_type !== "apartment" && (
                    <span style={{ background: isCom ? `${h.gn}12` : `${h.in}12`, color: isCom ? h.gn : h.in, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>
                      {ptLabel(p.property_type, t)}
                    </span>
                  )}
                  {p.amen.slice(0, 3).map(a => (
                    <span key={a} style={{ background: h.dk ? "rgba(255,255,255,0.06)" : "rgba(17,24,39,0.045)", color: h.ts, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 500 }}>{a}</span>
                  ))}
                  {p.amen.length > 3 && (
                    <span style={{ color: h.tt || h.sc, fontSize: 10, fontWeight: 600, alignSelf: "center" }}>+{p.amen.length - 3}</span>
                  )}
                </div>

                {/* tenant row */}
                {te && (
                  <div
                    onClick={() => { localStorage.setItem("editTid", te.id); go("tenantDetail"); }}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      cursor: "pointer", marginTop: 12, padding: "9px 11px",
                      background: h.dk ? "rgba(255,255,255,0.04)" : "rgba(17,24,39,0.035)",
                      borderRadius: 14, transition: "background 0.15s",
                    }}
                    title="View tenant details"
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                      <Avatar src={te.avatar_url} name={te.name} size={30} fontSize={12} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: h.tx, fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{te.name}</div>
                        <div style={{ color: h.ts, fontSize: 10.5 }}>{t.since} {te.join}</div>
                      </div>
                    </div>
                    <span style={{ color: h.tt || h.sc, fontSize: 15, fontWeight: 700, flexShrink: 0 }}>{"›"}</span>
                  </div>
                )}

                {/* action chips */}
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button onClick={() => { setSelPropId(p.id); go("propDetail"); }}
                    style={{ flex: 1, background: `${h.ac}0f`, border: "none", color: h.a2, padding: "9px", borderRadius: 12, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {t.viewDetails || "View Details"}
                  </button>
                  <button onClick={() => { setSelPropId(p.id); go("editProp"); }}
                    style={{ flex: 1, background: h.dk ? "rgba(255,255,255,0.06)" : "rgba(17,24,39,0.045)", border: "none", color: h.ts, padding: "9px", borderRadius: 12, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {t.edit}
                  </button>
                  <button onClick={() => deleteProp(p.id)} aria-label={t.delete}
                    style={{ background: `${h.rd}0f`, border: "none", color: h.rd, padding: "9px 14px", borderRadius: 12, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {"✕"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PropsListPage;
